import { LinearProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { restoreToken } from "common/utils/token";
import { useLocation, useNavigate } from "react-router-dom";
import { AnalyticsEvents, mixpanel } from "../common/analytics";

import { useFormik } from "formik";

import { FullScreenDialog } from "common/components";
import {
  changePassword,
  fetchTokenAndStore,
  initializeApp,
  initializeFarm,
} from "services";
import { useAppStore } from "stores";
import PasswordResetUserDetails from "./PasswordResetUserDetails";

import { useTranslation } from "common/locales";
import { useEffect, useState } from "react";
import { useNotification } from "./NotificationProvider";

export interface FormValues {
  mail: string;
  password: string;
  confirmPassword: string;
}

export interface FormErrors {
  confirmPassword?: string | undefined;
}

export default function SignupDialog() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppStore((state) => state.user);
  const creatingUser = useAppStore((state) => state.isLoggingIn);
  const appInitialising = useAppStore((state) => state.loading);

  const loading = creatingUser || appInitialising === "pending";
  const { notify } = useNotification();

  useEffect(() => {
    if (restoreToken(location)) {
      const workaroundFunc = async () => {
        try {
          await initializeApp();
        } catch (e) {
          console.log(e);
        }
      };
      void workaroundFunc();
    }
  }, [location]);

  const { t } = useTranslation();
  const [passwordResetError, setPasswordResetError] = useState<boolean>(false);
  const [passwordResetInProgress, setPasswordResetInProgress] =
    useState<boolean>(false);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      mail: user?.mail || "",
      password: "",
      confirmPassword: "",
    },
    onSubmit: () => {
      void handlePasswordReset();
    },
    validate: (values) => {
      setPasswordResetError(false);
      const errors: FormErrors = {};
      if (values.password !== values.confirmPassword) {
        errors.confirmPassword = t("signup.confirmPassword.error");
      }
      return errors;
    },
  });

  restoreToken(location);

  const handlePasswordReset = async () => {
    try {
      if (formik.values.password) {
        setPasswordResetInProgress(true);
        await changePassword(formik.values.password);
        notify(t("passwordChange.success"));

        mixpanel.track(AnalyticsEvents.PASSWORD_RESET_COMPLETED);

        await fetchTokenAndStore({
          mail: formik.values.mail,
          password: formik.values.password,
        });
        const res = await initializeApp();
        const farms = res?.farms;
        if (farms && farms.length > 0) {
          const defaultFarm = farms[0];
          await initializeFarm(defaultFarm);
          navigate(`/farm/${defaultFarm.guid}`);
        } else {
          navigate("/add-farm/enter-name");
        }
      }
    } catch (err) {
      console.log(err);
      setPasswordResetError(true);
      notify(t("passwordChange.failure"));
      mixpanel.track(AnalyticsEvents.PASSWORD_RESET_FAILED);
    } finally {
      setPasswordResetInProgress(false);
    }
  };

  return (
    <FullScreenDialog>
      {(loading || passwordResetInProgress) && <LinearProgress />}

      <PasswordResetUserDetails
        values={formik.values}
        errors={formik.errors}
        handleChange={formik.handleChange}
        handleSubmit={formik.handleSubmit}
        passwordUpdateError={passwordResetError}
        passwordResetInProgress={passwordResetInProgress}
      />
    </FullScreenDialog>
  );
}

export const StyledForm = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  margin: "auto",
  width: "250px",
  paddingBottom: theme.spacing(2),
}));
